import { z } from 'zod';

import { type QuizQuoteFormValues } from './quizQuote.schema';
import { quoteDateSchema } from './quoteDate';

const amountSchema = z.number().finite().nonnegative();
const discountSchema = z.object({
  code: z.string().min(1),
  label: z.string().min(1),
  rate: z.number().finite().min(0).max(1).nullable().optional(),
  amount: amountSchema,
});
const pricingTypeNoticeSchema = z
  .object({
    code: z.string().min(1),
    message: z.string().min(1),
  })
  .nullish();

const issuedQuizQuoteSchema = z
  .object({
    recipient: z.object({
      organizationName: z.string().min(1),
      contactName: z.string().min(1),
      phone: z.string().min(1),
      email: z.string().email(),
      annotation: z.string().optional(),
    }),
    product: z.object({
      plan: z.enum(['basic', 'pro']),
      requestedPricingType: z.enum(['individual', 'group']),
      pricingType: z.enum(['individual', 'group']),
      pricingTypeNotice: pricingTypeNoticeSchema,
      userCount: z.number().int().positive(),
      monthCount: z.number().int().positive(),
      startDate: quoteDateSchema,
      endDate: quoteDateSchema,
    }),
    pricing: z.object({
      currency: z.literal('KRW'),
      regularUnitAmount: amountSchema,
      originalAmount: amountSchema,
      discountAmount: amountSchema,
      finalAmount: amountSchema,
      discounts: z.array(discountSchema).max(1),
    }),
    issuedAt: z.string().datetime(),
    validUntil: z.string().datetime(),
  })
  .superRefine(({ product, pricing }, ctx) => {
    if (
      product.requestedPricingType !== product.pricingType &&
      !product.pricingTypeNotice
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['product', 'pricingTypeNotice'],
        message: '적용 요금 유형이 변경되면 안내가 필요합니다.',
      });
    }

    if (
      pricing.regularUnitAmount * product.userCount * product.monthCount !==
      pricing.originalAmount
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pricing', 'originalAmount'],
        message: '견적 정가가 단가, 인원, 기간과 일치하지 않습니다.',
      });
    }

    if (
      pricing.originalAmount - pricing.discountAmount !==
      pricing.finalAmount
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pricing', 'finalAmount'],
        message: '견적 금액 합계가 일치하지 않습니다.',
      });
    }

    const appliedDiscountAmount = pricing.discounts.reduce(
      (sum, discount) => sum + discount.amount,
      0,
    );

    if (appliedDiscountAmount !== pricing.discountAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pricing', 'discounts'],
        message: '할인 상세와 총 할인 금액이 일치하지 않습니다.',
      });
    }

    if (product.endDate !== addMonths(product.startDate, product.monthCount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['product', 'endDate'],
        message: '견적 종료일이 시작일과 기간에 일치하지 않습니다.',
      });
    }
  });

export type IssuedQuizQuote = z.infer<typeof issuedQuizQuoteSchema>;

export const QUOTE_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type QuoteUtmParams = Partial<
  Record<(typeof QUOTE_UTM_KEYS)[number], string>
>;

/**
 * school 서버가 받는 요청 shape. 필드명(`planType`·`term`)과 `quoted*` 는 Django 폼
 * 계약이라 임의로 바꾸면 안 된다 — CRM 이관 전까지 이 이름을 유지한다.
 */
export interface QuizQuoteApiRequest {
  schoolName: string;
  userCount: number;
  term: number;
  startDate: string;
  plan: 'basic' | 'pro';
  planType: 'individual' | 'group';
  accountName: string;
  accountPhone: string;
  accountEmail: string;
  annotation?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  quotedAmount?: number;
  quotedRegularAmount?: number;
  quotedTotalAmount?: number;
  quotedDiscountAmount?: number;
  quotedFinalAmount?: number;
}

export interface QuizQuoteApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    fields?: Partial<Record<keyof QuizQuoteApiRequest, string>>;
    raw?: unknown;
  };
}

export class QuizQuoteApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: QuizQuoteApiErrorBody,
  ) {
    super(body.error?.message || '견적서 발행에 실패했습니다.');
    this.name = 'QuizQuoteApiError';
  }
}

function addMonths(date: string, monthCount: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const targetMonth = month + monthCount;
  const targetYear = year + Math.floor((targetMonth - 1) / 12);
  const normalizedMonth = ((targetMonth - 1) % 12) + 1;
  const lastDay = new Date(
    Date.UTC(targetYear, normalizedMonth, 0),
  ).getUTCDate();
  const targetDay = Math.min(day, lastDay);

  return `${targetYear.toString().padStart(4, '0')}-${normalizedMonth
    .toString()
    .padStart(2, '0')}-${targetDay.toString().padStart(2, '0')}`;
}

export function parseIssuedQuizQuote(value: unknown): IssuedQuizQuote {
  return issuedQuizQuoteSchema.parse(value);
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (_error) {
    return {
      error: {
        code: 'invalid_json_response',
        message: '견적서 발행 서버 응답을 해석할 수 없습니다.',
        raw: text,
      },
    };
  }
}

/**
 * 클라이언트가 만든 견적서를 school 요청 shape 으로 옮긴다.
 *
 * `planType` 은 **사용자가 요청한** 유형을 그대로 보낸다(적용된 `pricingType` 이 아니라).
 * 단체 조건 미달 시 개인 요금으로 낮추는 판정은 클라가 하고, school 은 신청 내용을
 * 기록하는 쪽이라 옛 동작과 같게 유지한다.
 */
export function toQuizQuoteNotificationRequest(
  values: QuizQuoteFormValues,
  utm: QuoteUtmParams,
  quote: IssuedQuizQuote,
): QuizQuoteApiRequest {
  const { pricing, product } = quote;
  const unitCount = product.userCount * product.monthCount;

  return {
    schoolName: values.school_name,
    accountName: values.account_name,
    accountPhone: values.account_phone,
    accountEmail: values.account_email,
    plan: values.plan,
    planType: values.plan_type,
    userCount: values.user_count,
    term: values.term,
    startDate: values.start_date,
    annotation: values.annotation || undefined,
    utmSource: utm.utm_source,
    utmMedium: utm.utm_medium,
    utmCampaign: utm.utm_campaign,
    utmContent: utm.utm_content,
    utmTerm: utm.utm_term,
    // 옛 `amount` = 할인 적용 단가. 정률 할인엔 딱 떨어지는 단가가 없어 역산한다.
    quotedAmount:
      unitCount > 0 ? Math.round(pricing.finalAmount / unitCount) : 0,
    quotedRegularAmount: pricing.regularUnitAmount,
    quotedTotalAmount: pricing.originalAmount,
    quotedDiscountAmount: pricing.discountAmount,
    quotedFinalAmount: pricing.finalAmount,
  };
}

/**
 * 견적 신청을 school 서버에 통보한다. 응답은 쓰지 않는다 — 견적서는 클라가
 * `createQuizQuote()` 로 이미 확정했다.
 *
 * ⚠️ 브라우저에서 zepschool.us 를 직접 부르지 않는 이유: 프리플라이트가 405 로 떨어져
 * (ACAO 헤더 없음) CORS 에 막힌다. `pages/api/pricing/quote.api.ts` 프록시를 경유한다.
 */
export async function sendQuizQuoteNotification(
  request: QuizQuoteApiRequest,
): Promise<void> {
  const response = await fetch('/api/pricing/quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw new QuizQuoteApiError(response.status, body as QuizQuoteApiErrorBody);
  }
}
